import dayjs from 'dayjs';
import { Form } from 'antd';
import { hasGlKey } from '../LeaseUtils/helper';
import { useEffect, useMemo, useState } from 'react';
import { activeInactiveStatus } from 'pages/globals/global';
import { CardHeader, Stack, Typography } from '@mui/material';
import DataTableV8 from 'components/LynDataTable/DataTableV8';
import LeaseHistoryDetailModal from './LeaseHistoryDetailModal';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';

const headerSX = {
	p: '10px',
	mb: 3,
	cursor: 'pointer',
	backgroundColor: '#e1f0fb !important',

	'& .MuiCardHeader-action': {
		m: '0px auto',
		alignSelf: 'center',
	},
};

const LeaseHistoryTable = () => {
	const form = Form.useFormInstance();
	const leaseId = form.getFieldValue('leaseId');

	const [showModal, setShowModal] = useState(false);
	const [selectHistory, setSelectHistory] = useState(null);
	const [paginationModify, setPaginationModify] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [paginationEdit, setPaginationEdit] = useState({
		pageIndex: 0,
		pageSize: 10,
	});

	const [getLeaseHistoryData, { data: leaseHistoryData, isLoading: isLeaseHistoryDataLoading }] = useSendControlDataMutation();

	const COLUMNS = useMemo(
		() => [
			{
				header: 'Lease ID',
				accessorKey: 'leaseId',
				id: 'leaseId',
				accessorFn: (row) => row.data?.leaseId || row.leaseId || 'N/A',
				dataType: 'text',
			},
			{
				header: 'Lease Description',
				accessorKey: 'leaseTitle',
				id: 'leaseTitle',
				accessorFn: (row) => row.data?.leaseTitle || row.leaseTitle || 'N/A',
				dataType: 'text',
			},
			{
				header: 'Name of Lessor',
				accessorKey: 'lessorName',
				id: 'lessorName',
				accessorFn: (row) => row.data?.lessorName || row.lessorName || 'N/A',
				dataType: 'text',
			},
			{
				header: 'Lease Start Date',
				accessorKey: 'leaseStartDate',
				id: 'leaseStartDate',
				accessorFn: (row) => {
					const startDate = row.data?.leaseStartDate || row.leaseStartDate;
					return startDate ? dayjs(startDate).format('YYYY-MM-DD') : 'N/A';
				},
			},
			{
				header: 'Lease End Date',
				accessorKey: 'leaseEndDate',
				id: 'leaseEndDate',
				accessorFn: (row) => {
					const endDate = row.data?.leaseEndDate || row.leaseEndDate;
					return endDate ? dayjs(endDate).format('YYYY-MM-DD') : 'N/A';
				},
			},
			{
				header: 'Lease Term',
				accessorKey: 'leaseTerm',
				id: 'leaseTerm',
				accessorFn: (row) => row.data?.leaseTerm || row.leaseTerm || 'N/A',
			},
			{
				header: 'Payment Modality',
				accessorKey: 'paymentModality',
				id: 'paymentModality',
				accessorFn: (row) => {
					const value = row.data?.paymentModality || row.paymentModality;
					return value === 'Start' ? 'Advance Basis' : 'Accrual Basis';
				},
			},
			{
				header: 'Payment Frequency',
				accessorKey: 'paymentFrequency',
				id: 'paymentFrequency',
				accessorFn: (row) => row.data?.paymentFrequency || row.paymentFrequency || 'N/A',
			},
			{
				header: 'IBR',
				accessorKey: 'initialDiscountRate',
				id: 'initialDiscountRate',
				accessorFn: (row) => row.data?.initialDiscountRate || row.initialDiscountRate || 'N/A',
			},
			{
				header: 'GL Assignment',
				accessorKey: 'glAssignment',
				id: 'glAssignment',
				accessorFn: (row) => (hasGlKey(row?.data) ? 'Complete' : 'Incomplete'),
				cell: ({ getValue }) => activeInactiveStatus(getValue()),
			},
			{
				header: 'CC Assignment',
				accessorKey: 'ccAssignment',
				id: 'ccAssignment',
				accessorFn: (row) => {
					const ccAssignment = row.data?.refCCAssignmentIdentityId || row.refCCAssignmentIdentityId;
					return ccAssignment?.date ? 'Complete' : 'Incomplete';
				},
				cell: ({ getValue }) => activeInactiveStatus(getValue()),
			},
			{
				header: 'Agreement Uploaded',
				accessorKey: 'agreementUploaded',
				id: 'agreementUploaded',
				accessorFn: (row) => {
					const agreementFiles = row.data?.agreementFiles || row.agreementFiles;
					return agreementFiles?.length > 0 ? 'Complete' : 'Incomplete';
				},
				cell: ({ getValue }) => {
					const value = getValue();
					return value === 'Complete' ? activeInactiveStatus(value) : activeInactiveStatus(`${value}WithWarning`);
				},
			},
			{
				header: 'Status',
				accessorKey: 'activationStatus',
				id: 'activationStatus',
				accessorFn: (row) => row.activationStatus || 'N/A',
				cell: ({ getValue }) => activeInactiveStatus(getValue()),
			},
		],
		[]
	);

	useEffect(() => {
		if (leaseId) {
			const payload = { leaseId };
			const convertBase64 = btoa(JSON.stringify(payload));

			getLeaseHistoryData({
				payload,
				data: convertBase64,
				processCode: '10147',
				scriptName: 'lease-edit-modify-history',
			});
		}
	}, [leaseId]);

	const modifiedData = leaseHistoryData?.responseData?.modifiedData || [];
	const editData = leaseHistoryData?.responseData?.editData || [];

	return (
		<>
			<CardHeader
				sx={{ ...headerSX }}
				titleTypographyProps={{ variant: 'subtitle1' }}
				title={
					<Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
						<Typography variant="subtitle1">Modify History</Typography>
					</Stack>
				}
			/>

			<DataTableV8
				manualPagination
				COLUMNS={COLUMNS}
				DATA={modifiedData}
				firstColStrick={false}
				pagination={paginationModify}
				totalCount={modifiedData.length}
				isLoading={isLeaseHistoryDataLoading}
				xlsxFileName={`${leaseId}_modifiedData`}
				onPaginationChange={setPaginationModify}
				onClickHandler={(data) => {
					setShowModal(true);
					setSelectHistory(data);
				}}
			/>

			<CardHeader
				sx={{ ...headerSX, mt: 3 }}
				titleTypographyProps={{ variant: 'subtitle1' }}
				title={
					<Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
						<Typography variant="subtitle1">Edit History</Typography>
					</Stack>
				}
			/>

			<DataTableV8
				DATA={editData}
				manualPagination
				COLUMNS={COLUMNS}
				firstColStrick={false}
				pagination={paginationEdit}
				totalCount={editData.length}
				xlsxFileName={`${leaseId}_editData`}
				isLoading={isLeaseHistoryDataLoading}
				onPaginationChange={setPaginationEdit}
				onClickHandler={(data) => {
					setShowModal(true);
					setSelectHistory(data);
				}}
			/>

			{showModal && <LeaseHistoryDetailModal showModal={showModal} setShowModal={setShowModal} selectHistory={selectHistory} />}
		</>
	);
};

export default LeaseHistoryTable;
